'use client';

import { c as _c } from "react/compiler-runtime";
import { useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container } from '@/app/components/layouts/container';
import { APP_VIDEO_DEFAULTS, AppVideo } from '@/app/components/media/app-video';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { useSectionExit } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/section-slider/useSectionExit';
import { hasPlayableData, playVideoFromGesture, primeVideo } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/autoplay';
import { useHeroClipQueueEntry } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/preload-queue/clipQueue';
import { useHeroPlayUnlock } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useHeroPlayUnlock';
import { useSectionVideoProgress } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useSectionVideoProgress';
import { useVideoBuffering } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useVideoBuffering';
import { useVideoPlayback } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useVideoPlayback';
import { useVideoPreload } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useVideoPreload';
import { BOX_STAGGER_MS, CLIP_CROSSFADE_MS, INDICATORS, SPEED_VIDEO_END_PROGRESS, VALUE_CLIMB_MS } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/speed-key-messages/config';
import { ProgressGlow } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/speed-key-messages/progress-glow';
import { ProgressRing } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/speed-key-messages/progress-ring';
import { type SpeedKeyMessageIndicator, type SpeedKeyMessagesProps } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/speed-key-messages/types';
import { SECTION_VIDEO_BOX_CLASSNAME } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/styles';
import { SPARTAN_ENGINE_HERO_TRACKING } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/tracking';
import { SECTION_EXIT_SECONDS, VIDEO_COVER_FALLBACK_SECONDS } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/constants';
import styles from '@/app/lcv/components/sections/spartan/engine/styles.module.css';
import { SharedAnimatedValue } from '@/app/lcv/components/sections/spartan/shared/animated-value';
import { ENGINE_SPEED_SECTION_DATA } from '@/app/lcv/contents/spartan/engine/hero.data';
import { ENGINE_COMPARISON_LIST } from '@/app/lcv/contents/spartan/engine/shared/22biturbo-specifications.data';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { buildHtmlMarkup } from '@/app/utils/html/html';
import { reportRumWarning } from '@/app/utils/reportRum';
import logger from '@/lib/logger';
const INDICATOR_COMPONENTS = {
  ring: ProgressRing,
  glow: ProgressGlow
} satisfies Record<SpeedKeyMessageIndicator, unknown>;
export const SpeedKeyMessages = t0 => {
  const $ = _c(120);
  const {
    phase,
    onExitEnd,
    isActive,
    index,
    videoProgressRef,
    isProgressWatched,
    playMode,
    onAnimationEnd,
    onBlackModeChange,
    onPlaybackStarted,
    onVideoEnded,
    keepLoopPlaying: t1,
    canLoadMain: t2,
    onVideoBlocked,
    onBufferingChange,
    registerPlayUnlock
  } = t0;
  const keepLoopPlaying = t1 === undefined ? false : t1;
  const canLoadMain = t2 === undefined ? true : t2;
  const isReducedMotion = useReducedMotion();
  const mainVideoRef = useRef(null);
  const endLoopVideoRef = useRef(null);
  const keyMessageBarRef = useRef(null);
  const bgGradientRef = useRef(null);
  const [isMainReady, setIsMainReady] = useState(false);
  const [isEndLoopReady, setIsEndLoopReady] = useState(false);
  let t3;
  if ($[0] !== playMode) {
    t3 = () => playMode === "final";
    $[0] = playMode;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [hasMainEnded, setHasMainEnded] = useState(t3);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      main: ENGINE_SPEED_SECTION_DATA.videoSources,
      endLoop: ENGINE_SPEED_SECTION_DATA.endLoopVideoSources
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const sources = t4;
  const shouldPlayClips = playMode === "full";
  const isDesktop = !!useMediaQuery(APP_VIDEO_DEFAULTS.desktopMedia);
  const device = isDesktop ? "desktop" : "mobile";
  useHeroClipQueueEntry("speedMain", sources.main.video[device]);
  useHeroClipQueueEntry("speedEndLoop", sources.endLoop.video[device]);
  const mainClip = useVideoPreload(sources.main.video[device], shouldPlayClips && canLoadMain);
  const t5 = isActive && shouldPlayClips && isProgressWatched;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [{
      videoRef: mainVideoRef,
      endProgress: SPEED_VIDEO_END_PROGRESS
    }];
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== index || $[5] !== t5 || $[6] !== videoProgressRef) {
    t7 = {
      index,
      progressRef: videoProgressRef,
      enabled: t5,
      clips: t6
    };
    $[4] = index;
    $[5] = t5;
    $[6] = videoProgressRef;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  useSectionVideoProgress(t7);
  let t8;
  if ($[8] !== onExitEnd || $[9] !== phase) {
    t8 = {
      phase,
      onExitEnd,
      durationSeconds: SECTION_EXIT_SECONDS
    };
    $[8] = onExitEnd;
    $[9] = phase;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  useSectionExit(t8);
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = video => {
      if (!video) {
        return;
      }
      mainVideoRef.current = video;
      setIsMainReady(true);
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const handleMainReady = t9;
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      logger.warn("[engine] speed video failed, skipped");
      reportRumWarning("spartan_speed_video_failed", {
        clip: "main"
      });
      setHasMainEnded(true);
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const handleMainError = t10;
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = video_0 => {
      if (!video_0) {
        return;
      }
      endLoopVideoRef.current = video_0;
      setIsEndLoopReady(true);
    };
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const handleEndLoopReady = t11;
  const t12 = isActive && shouldPlayClips && !hasMainEnded && isMainReady;
  let t13;
  if ($[14] !== onVideoBlocked || $[15] !== t12) {
    t13 = {
      videoRef: mainVideoRef,
      shouldPlay: t12,
      onPlayBlocked: onVideoBlocked
    };
    $[14] = onVideoBlocked;
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const isMainPlaying = useVideoPlayback(t13);
  const t14 = (isActive || phase === "exiting" || keepLoopPlaying) && hasMainEnded && isEndLoopReady;
  let t15;
  if ($[17] !== onVideoBlocked || $[18] !== t14) {
    t15 = {
      videoRef: endLoopVideoRef,
      shouldPlay: t14,
      onPlayBlocked: onVideoBlocked
    };
    $[17] = onVideoBlocked;
    $[18] = t14;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  const isEndLoopPlaying = useVideoPlayback(t15);
  let t16;
  if ($[20] !== hasMainEnded) {
    t16 = isActiveNow => {
      const current = hasMainEnded ? endLoopVideoRef : mainVideoRef;
      const next = hasMainEnded ? mainVideoRef : endLoopVideoRef;
      if (isActiveNow) {
        playVideoFromGesture(current.current);
      } else {
        primeVideo(current.current);
      }
      primeVideo(next.current);
    };
    $[20] = hasMainEnded;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  useHeroPlayUnlock(registerPlayUnlock, {
    unlock: t16,
    isClipOnHand: () => hasMainEnded ? endLoopClip.isResident || hasPlayableData(endLoopVideoRef.current) : mainClip.isResident || hasPlayableData(mainVideoRef.current)
  });
  const t17 = isActive && isMainPlaying && !hasMainEnded;
  let t18;
  if ($[22] !== t17) {
    t18 = {
      videoRef: mainVideoRef,
      enabled: t17
    };
    $[22] = t17;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  const isMainBuffering = useVideoBuffering(t18);
  const bufferingChangeRef = useRef(onBufferingChange);
  let t19;
  if ($[24] !== onBufferingChange) {
    t19 = () => {
      bufferingChangeRef.current = onBufferingChange;
    };
    $[24] = onBufferingChange;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  useEffect(t19);
  let t20;
  let t21;
  if ($[26] !== isMainBuffering) {
    t20 = () => {
      if (!isMainBuffering) {
        return;
      }
      bufferingChangeRef.current?.(true);
      return () => bufferingChangeRef.current?.(false);
    };
    t21 = [isMainBuffering];
    $[26] = isMainBuffering;
    $[27] = t20;
    $[28] = t21;
  } else {
    t20 = $[27];
    t21 = $[28];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[29] !== hasMainEnded || $[30] !== isMainPlaying) {
    t22 = () => {
      if (!isMainPlaying || hasMainEnded) {
        return;
      }
      const element = mainVideoRef.current;
      if (!element) {
        return;
      }
      let raf = 0;
      const checkProgress = () => {
        const {
          currentTime,
          duration,
          ended
        } = element;
        const hasReachedEnd = ended || duration > 0 && currentTime >= duration * SPEED_VIDEO_END_PROGRESS;
        if (hasReachedEnd) {
          setHasMainEnded(true);
          cancelAnimationFrame(raf);
          return;
        }
        raf = requestAnimationFrame(checkProgress);
      };
      raf = requestAnimationFrame(checkProgress);
      return () => cancelAnimationFrame(raf);
    };
    t23 = [isMainPlaying, hasMainEnded];
    $[29] = hasMainEnded;
    $[30] = isMainPlaying;
    $[31] = t22;
    $[32] = t23;
  } else {
    t22 = $[31];
    t23 = $[32];
  }
  useEffect(t22, t23);
  let t24;
  if ($[33] !== playMode) {
    t24 = () => playMode === "final";
    $[33] = playMode;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  const [canLoadEndLoop, setCanLoadEndLoop] = useState(t24);
  let t25;
  let t26;
  if ($[35] !== hasMainEnded || $[36] !== isMainPlaying) {
    t25 = () => {
      if (isMainPlaying || hasMainEnded) {
        setCanLoadEndLoop(true);
      }
    };
    t26 = [isMainPlaying, hasMainEnded];
    $[35] = hasMainEnded;
    $[36] = isMainPlaying;
    $[37] = t25;
    $[38] = t26;
  } else {
    t25 = $[37];
    t26 = $[38];
  }
  useEffect(t25, t26);
  const endLoopClip = useVideoPreload(sources.endLoop.video[device], canLoadEndLoop);
  const blackModeRef = useRef(onBlackModeChange);
  const animationEndRef = useRef(onAnimationEnd);
  const videoEndedRef = useRef(onVideoEnded);
  let t27;
  if ($[39] !== onAnimationEnd || $[40] !== onBlackModeChange || $[41] !== onVideoEnded) {
    t27 = () => {
      blackModeRef.current = onBlackModeChange;
      animationEndRef.current = onAnimationEnd;
      videoEndedRef.current = onVideoEnded;
    };
    $[39] = onAnimationEnd;
    $[40] = onBlackModeChange;
    $[41] = onVideoEnded;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  useEffect(t27);
  let t28;
  let t29;
  if ($[43] !== isActive || $[44] !== shouldPlayClips) {
    t28 = () => {
      if (!isActive || !shouldPlayClips) {
        return;
      }
      blackModeRef.current("cover");
    };
    t29 = [isActive, shouldPlayClips];
    $[43] = isActive;
    $[44] = shouldPlayClips;
    $[45] = t28;
    $[46] = t29;
  } else {
    t28 = $[45];
    t29 = $[46];
  }
  useEffect(t28, t29);
  let t30;
  let t31;
  if ($[47] !== hasMainEnded || $[48] !== shouldPlayClips) {
    t30 = () => {
      if (!shouldPlayClips || !hasMainEnded) {
        return;
      }
      videoEndedRef.current?.();
    };
    t31 = [shouldPlayClips, hasMainEnded];
    $[47] = hasMainEnded;
    $[48] = shouldPlayClips;
    $[49] = t30;
    $[50] = t31;
  } else {
    t30 = $[49];
    t31 = $[50];
  }
  useEffect(t30, t31);
  const isPictureRunning = isMainPlaying || isEndLoopPlaying;
  let t32;
  let t33;
  if ($[51] !== isPictureRunning || $[52] !== onPlaybackStarted) {
    t32 = () => {
      if (isPictureRunning) {
        onPlaybackStarted?.();
      }
    };
    t33 = [isPictureRunning, onPlaybackStarted];
    $[51] = isPictureRunning;
    $[52] = onPlaybackStarted;
    $[53] = t32;
    $[54] = t33;
  } else {
    t32 = $[53];
    t33 = $[54];
  }
  useEffect(t32, t33);
  let t34;
  let t35;
  if ($[55] !== isActive || $[56] !== isPictureRunning) {
    t34 = () => {
      if (!isActive) {
        return;
      }
      if (isPictureRunning) {
        blackModeRef.current("none");
        return;
      }
      const timer = window.setTimeout(() => blackModeRef.current("none"), VIDEO_COVER_FALLBACK_SECONDS * 1000);
      return () => window.clearTimeout(timer);
    };
    t35 = [isActive, isPictureRunning];
    $[55] = isActive;
    $[56] = isPictureRunning;
    $[57] = t34;
    $[58] = t35;
  } else {
    t34 = $[57];
    t35 = $[58];
  }
  useEffect(t34, t35);
  let t36;
  if ($[59] !== playMode) {
    t36 = () => playMode === "final" ? ENGINE_COMPARISON_LIST.length : 0;
    $[59] = playMode;
    $[60] = t36;
  } else {
    t36 = $[60];
  }
  const [revealedCount, setRevealedCount] = useState(t36);
  let t37;
  if ($[61] !== playMode) {
    t37 = () => playMode === "final";
    $[61] = playMode;
    $[62] = t37;
  } else {
    t37 = $[62];
  }
  const [isHeadlineRevealed, setIsHeadlineRevealed] = useState(t37);
  let t38;
  let t39;
  if ($[63] !== hasMainEnded || $[64] !== isReducedMotion) {
    t38 = () => {
      if (!hasMainEnded) {
        return;
      }
      if (!!isReducedMotion) {
        setRevealedCount(ENGINE_COMPARISON_LIST.length);
        setIsHeadlineRevealed(true);
        return;
      }
      setIsHeadlineRevealed(true);
      const timers = ENGINE_COMPARISON_LIST.map((_, index_0) => window.setTimeout(() => setRevealedCount(count => Math.max(count, index_0 + 1)), index_0 * BOX_STAGGER_MS));
      return () => timers.forEach(_temp);
    };
    t39 = [hasMainEnded, isReducedMotion];
    $[63] = hasMainEnded;
    $[64] = isReducedMotion;
    $[65] = t38;
    $[66] = t39;
  } else {
    t38 = $[65];
    t39 = $[66];
  }
  useEffect(t38, t39);
  let t40;
  let t41;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = () => {
      const bar = keyMessageBarRef.current;
      const gradient = bgGradientRef.current;
      if (!bar || !gradient) {
        return;
      }
      const observer = new ResizeObserver(() => {
        gradient.style.setProperty("--speed-bar-h", `${bar.offsetHeight}px`);
      });
      observer.observe(bar);
      return () => observer.disconnect();
    };
    t41 = [];
    $[67] = t40;
    $[68] = t41;
  } else {
    t40 = $[67];
    t41 = $[68];
  }
  useEffect(t40, t41);
  let t42;
  let t43;
  if ($[69] !== isActive || $[70] !== isHeadlineRevealed) {
    t42 = () => {
      if (!isActive || !isHeadlineRevealed) {
        return;
      }
      animationEndRef.current();
    };
    t43 = [isActive, isHeadlineRevealed];
    $[69] = isActive;
    $[70] = isHeadlineRevealed;
    $[71] = t42;
    $[72] = t43;
  } else {
    t42 = $[71];
    t43 = $[72];
  }
  useEffect(t42, t43);
  const t44 = endLoopClip.src ?? sources.endLoop.video;
  let t45;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = {
      src: sources.endLoop.img.src,
      alt: sources.endLoop.img.alt
    };
    $[73] = t45;
  } else {
    t45 = $[73];
  }
  const t46 = !canLoadEndLoop || !endLoopClip.src;
  let t47;
  if ($[74] !== t44 || $[75] !== t46) {
    t47 = <div className={SECTION_VIDEO_BOX_CLASSNAME}><AppVideo video={t44} poster={t45} paused={false} loop={true} preload="auto" preventLoadVideo={t46} onReady={handleEndLoopReady} testId={SPARTAN_ENGINE_HERO_TRACKING.speedEndLoopVideo} /></div>;
    $[74] = t44;
    $[75] = t46;
    $[76] = t47;
  } else {
    t47 = $[76];
  }
  let t48;
  let t49;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = cn(SECTION_VIDEO_BOX_CLASSNAME, "transition-opacity ease-out");
    t49 = {
      transitionDuration: `${CLIP_CROSSFADE_MS}ms`
    };
    $[77] = t48;
    $[78] = t49;
  } else {
    t48 = $[77];
    t49 = $[78];
  }
  let t50;
  if ($[79] !== hasMainEnded) {
    t50 = cn("absolute inset-0 transition-opacity ease-out", {
      "pointer-events-none opacity-0": hasMainEnded
    });
    $[79] = hasMainEnded;
    $[80] = t50;
  } else {
    t50 = $[80];
  }
  let t51;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = {
      transitionDuration: `${CLIP_CROSSFADE_MS}ms`
    };
    $[81] = t51;
  } else {
    t51 = $[81];
  }
  const t52 = mainClip.src ?? sources.main.video;
  let t53;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = {
      src: sources.main.img.src,
      alt: sources.main.img.alt
    };
    $[82] = t53;
  } else {
    t53 = $[82];
  }
  const t54 = !shouldPlayClips || !canLoadMain || !mainClip.src;
  let t55;
  if ($[83] !== t52 || $[84] !== t54) {
    t55 = <AppVideo video={t52} poster={t53} paused={false} preload="auto" preventLoadVideo={t54} onElementMount={handleMainReady} onVideoError={handleMainError} testId={SPARTAN_ENGINE_HERO_TRACKING.speedVideo} />;
    $[83] = t52;
    $[84] = t54;
    $[85] = t55;
  } else {
    t55 = $[85];
  }
  let t56;
  if ($[86] !== t50 || $[87] !== t55) {
    t56 = <div className={t48} style={t49}><div className={t50} style={t51}>{t55}</div></div>;
    $[86] = t50;
    $[87] = t55;
    $[88] = t56;
  } else {
    t56 = $[88];
  }
  let t57;
  let t58;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <div className="absolute top-0 left-0 h-60 w-full bg-linear-to-b from-black to-transparent" />;
    t58 = cn(SECTION_VIDEO_BOX_CLASSNAME, "pointer-events-none");
    $[89] = t57;
    $[90] = t58;
  } else {
    t57 = $[89];
    t58 = $[90];
  }
  const t59 = !hasMainEnded;
  let t60;
  if ($[91] !== t59) {
    t60 = cn("absolute inset-x-0 bottom-0 transition-opacity duration-1000", styles.speedBgGradient, {
      "opacity-0": t59
    });
    $[91] = t59;
    $[92] = t60;
  } else {
    t60 = $[92];
  }
  let t61;
  if ($[93] !== t60) {
    t61 = <div className={t58}><div ref={bgGradientRef} className={t60} /></div>;
    $[93] = t60;
    $[94] = t61;
  } else {
    t61 = $[94];
  }
  const t62 = isHeadlineRevealed ? "scale-100" : "scale-125 opacity-0";
  let t63;
  if ($[95] !== t62) {
    t63 = cn("text-center text-white", "property-transforms-opacity ease-bounce duration-700", t62);
    $[95] = t62;
    $[96] = t63;
  } else {
    t63 = $[96];
  }
  let t64;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = <p className="text-sub-h1 font-noto-bold" dangerouslySetInnerHTML={buildHtmlMarkup(ENGINE_SPEED_SECTION_DATA.preTitle)} {...createDataAttributes({
      test: SPARTAN_ENGINE_HERO_TRACKING.speedPreTitle
    })} />;
    $[97] = t64;
  } else {
    t64 = $[97];
  }
  let t65;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t65 = <h2 className="text-h2 font-display" dangerouslySetInnerHTML={buildHtmlMarkup(ENGINE_SPEED_SECTION_DATA.title)} {...createDataAttributes({
      test: SPARTAN_ENGINE_HERO_TRACKING.speedTitle
    })} />;
    $[98] = t65;
  } else {
    t65 = $[98];
  }
  let t66;
  if ($[99] !== t63) {
    t66 = <div className="absolute inset-0 pt-20"><Container className={t63}>{t64}{t65}</Container></div>;
    $[99] = t63;
    $[100] = t66;
  } else {
    t66 = $[100];
  }
  let t67;
  let t68;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t67 = cn("flex flex-wrap justify-center gap-x-6 gap-y-4 lg:gap-x-12");
    t68 = createDataAttributes({
      test: SPARTAN_ENGINE_HERO_TRACKING.speedKeyMessageList
    });
    $[101] = t67;
    $[102] = t68;
  } else {
    t67 = $[101];
    t68 = $[102];
  }
  let t69;
  if ($[103] !== revealedCount) {
    t69 = ENGINE_COMPARISON_LIST.map((item, index_1) => {
      const isRevealed = index_1 < revealedCount;
      const Indicator = INDICATOR_COMPONENTS[INDICATORS[index_1] ?? "ring"];
      return <div key={item.description} className={cn("shrink-0", "property-opacity-translate ease-bounce duration-500", {
        "translate-y-4 opacity-0": !isRevealed
      })} {...createDataAttributes({
        test: SPARTAN_ENGINE_HERO_TRACKING.speedKeyMessage(index_1)
      })}><Indicator isFilled={isRevealed} className="w-35 shrink-0 lg:w-40"><p className={cn(styles.speedIndicatorTitle, "text-sub-h3 font-noto-bold text-white")} {...createDataAttributes({
            test: SPARTAN_ENGINE_HERO_TRACKING.speedKeyMessageTitle(index_1)
          })}>{item.title}</p><p className={cn(styles.speedIndicatorValue, "gap-1 text-white", {
            [styles.twoChar]: `${item.value}`.length === 2
          })} {...createDataAttributes({
            test: SPARTAN_ENGINE_HERO_TRACKING.speedKeyMessageValue(index_1)
          })}><SharedAnimatedValue className={cn("font-display", styles.value)} value={item.value} play={isRevealed} durationMs={VALUE_CLIMB_MS} /><span className={cn("font-noto text-body-1-fixed text-gray-400", "transition-opacity duration-300 ease-out", styles.percent, {
              "opacity-0": !isRevealed
            })}>%</span></p><p className={cn(styles.speedIndicatorCaption, "text-body-2 text-gray-400")} {...createDataAttributes({
            test: SPARTAN_ENGINE_HERO_TRACKING.speedKeyMessageDescription(index_1)
          })}>{item.description}</p></Indicator></div>;
    });
    $[103] = revealedCount;
    $[104] = t69;
  } else {
    t69 = $[104];
  }
  let t70;
  if ($[105] !== t69) {
    t70 = <Container className={t67} {...t68}>{t69}</Container>;
    $[105] = t69;
    $[106] = t70;
  } else {
    t70 = $[106];
  }
  const t71 = revealedCount < ENGINE_COMPARISON_LIST.length;
  let t72;
  if ($[107] !== t71) {
    t72 = cn("text-caption relative mt-4 text-gray-500 transition-opacity duration-300 ease-out", {
      "opacity-0": t71
    });
    $[107] = t71;
    $[108] = t72;
  } else {
    t72 = $[108];
  }
  let t73;
  if ($[109] !== t72) {
    t73 = <Container as="p" className={t72}>{ENGINE_SPEED_SECTION_DATA.remark}</Container>;
    $[109] = t72;
    $[110] = t73;
  } else {
    t73 = $[110];
  }
  let t74;
  if ($[111] !== t70 || $[112] !== t73) {
    t74 = <div ref={keyMessageBarRef} className="absolute bottom-0 left-0 w-full py-6 lg:py-12">{t70}{t73}</div>;
    $[111] = t70;
    $[112] = t73;
    $[113] = t74;
  } else {
    t74 = $[113];
  }
  let t75;
  if ($[114] !== t47 || $[115] !== t56 || $[116] !== t61 || $[117] !== t66 || $[118] !== t74) {
    t75 = <div className="relative h-full w-full">{t47}{t56}{t57}{t61}{t66}{t74}</div>;
    $[114] = t47;
    $[115] = t56;
    $[116] = t61;
    $[117] = t66;
    $[118] = t74;
    $[119] = t75;
  } else {
    t75 = $[119];
  }
  return t75;
};
function _temp(timer_0) {
  return window.clearTimeout(timer_0);
}